import { FC, useState, useEffect, useContext } from "react";
import { Button, Form, Card, Row, Col, Alert } from "react-bootstrap";
import _ from "lodash";
import moment from "moment";
import { useForm } from "react-hook-form";
import userApi from "../../../api/userApi";
import { AppContext } from "../../../context";
import { User } from "../../../types/User";
import Variable from "../../../utils/Variable";
import { formatUserName } from "../../../utils/auth";
import { formatMinutes } from "../../../utils/time";
import ActivityModal from "../Modals/ActivityModal";
import ChangeLogModal from "../Modals/ChangeLogModal";
import ImportHoursModal from "../Modals/ImportHoursModal";
import RecomputeDaysModal from "../Modals/RecomputeDaysModal";
import TimesheetBalanceModal from "../Modals/TimesheetBalanceModal";

interface Props {
  selectedUser: User;
  updateSelectedUser: (val: User) => void;
}

export interface UserForm {
  timesheetBalance: number;
  weeklyWorkMinutes: number;
  holidaysPerMonth?: number;
  availableHolidays?: number;
  incrementedHolidays: number;
}

const UserCard: FC<Props> = ({ selectedUser, updateSelectedUser }) => {
  const context = useContext(AppContext);
  const [updateLoading, toggleUpdateLoading] = useState(false);
  const [balanceModalLoading, toggleBalanceModal] = useState(false);
  const [activityModalShowing, toggleActivityModal] = useState(false);
  const [changeLogModalShwoing, toggleChangeLogModalModal] = useState(false);
  const [timesheetBalance, setTimesheetbalance] = useState(0);
  const { setValue, register, handleSubmit } = useForm<{ user: UserForm }>();

  const fetchBalance = async () => {
    try {
      const {
        data: { balance },
      } = await userApi.getBalance(selectedUser.id);

      setTimesheetbalance(balance);
    } catch {
      // eslint-disable-next-line no-console
      console.log("FETCH FAILED");
    }
  };

  useEffect(() => {
    if (selectedUser) {
      setValue("user", {
        timesheetBalance: selectedUser.timesheetBalance,
        weeklyWorkMinutes: selectedUser.weeklyWorkMinutes,
        holidaysPerMonth: selectedUser.holidaysPerMonth,
        availableHolidays: selectedUser.availableHolidays,
        incrementedHolidays: selectedUser.incrementedHolidays,
      });

      fetchBalance();
    }
  }, [selectedUser]);

  const submit = async (data: { user: UserForm }) => {
    try {
      toggleUpdateLoading(true);
      const formattedStuff = {};

      _.forEach(data.user, (val, key) => _.assign(formattedStuff, { [key]: +val }));

      const res = await userApi.adminEdit(selectedUser.id, formattedStuff as UserForm);
      toggleUpdateLoading(false);
      context.showToast({
        title: "Save success",
        message: "User info saved",
        variant: "success",
      });
      updateSelectedUser(res.data);
    } catch {
      toggleUpdateLoading(false);
      context.showToast({
        title: "Save failed",
        message: "Try again",
        variant: "danger",
      });
    }
  };

  return (
    <Card>
      <Form onSubmit={handleSubmit(submit)}>
        <Card.Header>
          <Card.Title>
            {formatUserName(selectedUser)} | {selectedUser.company.title}
          </Card.Title>
        </Card.Header>
        <Card.Body>
          <div className="flex-column d-flex align-items-start">
            {selectedUser.oauthSub && <span>Social login: {selectedUser.oauthSub}</span>}
            <span>Email: {selectedUser.email}</span>
            <span>Id: {selectedUser.id}</span>
            <span>Created at: {moment(selectedUser.createdAt).format("D.M.YYYY")}</span>
            <div className="mt-2">
              <Button onClick={() => toggleActivityModal(true)} size="sm" variant="secondary">
                View activity
              </Button>
              <Button onClick={() => toggleBalanceModal(true)} size="sm" variant="info" className="ms-2">
                Timesheet balance
              </Button>
              <Button onClick={() => toggleChangeLogModalModal(true)} size="sm" className="ms-2" variant="warning">
                View audit log
              </Button>
              <Variable initialValue={false}>
                {(importHoursModal, toggleImportHours) => {
                  return (
                    <>
                      <Button onClick={() => toggleImportHours(true)} size="sm" variant="light" className="ms-2">
                        Import hours
                      </Button>
                      <ImportHoursModal
                        user={selectedUser}
                        visible={importHoursModal}
                        onVisibleChange={() => toggleImportHours(false)}
                      />
                    </>
                  );
                }}
              </Variable>
              <Variable initialValue={false}>
                {(recomModalOpen, toggleRecomModal) => {
                  return (
                    <>
                      <Button onClick={() => toggleRecomModal(true)} size="sm" variant="primary" className="ms-2">
                        Recompute hours
                      </Button>
                      <RecomputeDaysModal
                        user={selectedUser}
                        visible={recomModalOpen}
                        onVisibleChange={() => {
                          toggleRecomModal(false);
                          fetchBalance();
                        }}
                      />
                    </>
                  );
                }}
              </Variable>
              <Button
                onClick={async () => {
                  const answer = prompt(
                    'Type "DELETE" to confirm deletion of all overtime data and manual edits for this user',
                  );
                  if (answer !== "DELETE") {
                    context.showToast({
                      title: "Cancelled",
                      message: "Overtime data deletion cancelled",
                      variant: "info",
                    });
                    return;
                  }
                  try {
                    toggleUpdateLoading(true);
                    await userApi.deleteOvertimeData(selectedUser.id);
                    await fetchBalance();
                    toggleUpdateLoading(false);
                    context.showToast({
                      title: "Success",
                      message: "Overtime data deleted",
                      variant: "success",
                    });
                  } catch (e) {
                    toggleUpdateLoading(false);
                    context.showToast({
                      title: "Delete failed",
                      message: e.message || "Unknown error",
                      variant: "danger",
                    });
                  }
                }}
                size="sm"
                variant="danger"
                className="ms-2"
                disabled={updateLoading}>
                Delete overtime data
              </Button>
            </div>
          </div>
          <Row className="mt-4">
            <Col md={6}>
              <Alert>
                Timesheet balance: <strong>{formatMinutes(timesheetBalance)}</strong> ({timesheetBalance}min)
              </Alert>
              <Form.Group className="mb-3">
                <Form.Label>Timesheet balance (minutes)</Form.Label>
                <Form.Control {...register("user.timesheetBalance")} />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Weekly work hours (minutes)</Form.Label>
                {selectedUser.worktimeGroup ? (
                  <div>
                    Worktime group: {selectedUser.worktimeGroup.title} ({selectedUser.worktimeGroup.weeklyWorkMinutes}
                    min)
                  </div>
                ) : (
                  <Form.Control {...register("user.weeklyWorkMinutes")} />
                )}
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Incremented holidays</Form.Label>
                <Form.Control {...register("user.incrementedHolidays")} />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Available holidays</Form.Label>
                <Form.Control {...register("user.availableHolidays")} />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Holidays per month</Form.Label>
                <Form.Control {...register("user.holidaysPerMonth")} />
              </Form.Group>
            </Col>
          </Row>
        </Card.Body>
        <Card.Footer className="d-flex justify-content-end">
          <Button disabled={updateLoading} variant="primary" type="submit">
            Save
          </Button>
        </Card.Footer>
      </Form>
      <ActivityModal visible={activityModalShowing} user={selectedUser} onVisibleChange={toggleActivityModal} />
      <TimesheetBalanceModal visible={balanceModalLoading} user={selectedUser} onVisibleChange={toggleBalanceModal} />
      <ChangeLogModal visible={changeLogModalShwoing} user={selectedUser} onVisibleChange={toggleChangeLogModalModal} />
    </Card>
  );
};

export default UserCard;
